import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from '@core/services/auth.service';

/**
 * Runs on every navigation (initial load, refresh, and bookmark/deep-link
 * access) for every route, including the home route. If a cached MSAL
 * account exists, it is not trusted on its own — a silent token acquisition
 * confirms the session is still live before any screen renders. This is what
 * catches the "closed the browser without logging out, came back later from
 * Favorites" scenario: the account is still cached, but the underlying
 * session is gone.
 *
 * - No cached account at all: allow navigation through. The home page and
 *   MsalGuard already handle the unauthenticated (not-yet-logged-in) case.
 * - Cached account + valid session: allow navigation through.
 * - Cached account + invalid session: block this navigation. AuthService's
 *   validateSession() has already routed the failure through SessionService,
 *   which shows the full-screen loader and redirects to Microsoft Entra ID
 *   login, so nothing is left blank.
 */
export const authSessionGuard: CanActivateFn = async () => {
  const msalService = inject(MsalService);
  const authService = inject(AuthService);

  const hasCachedAccount =
    msalService.instance.getActiveAccount() != null ||
    msalService.instance.getAllAccounts().length > 0;

  if (!hasCachedAccount) {
    return true;
  }

  return authService.validateSession();
};
